import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getCompanies, createCompany, updateCompany, deactivateCompany } from '../api/company';
import { Company } from '../types/Company';
import { useLanguage } from '../context/LanguageContext';
import Loader from '../components/Loader';
import EmptyState from '../components/EmptyState';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTES } from '../routes';

const CompanyManagement: React.FC = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const { t } = useLanguage();

    const [isCreateFormVisible, setCreateFormVisible] = useState(false);
    const [isEditModalOpen, setEditModalOpen] = useState(false);
    const [isDeactivateModalOpen, setDeactivateModalOpen] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

    const [newCompanyName, setNewCompanyName] = useState('');
    const [newCompanyDomain, setNewCompanyDomain] = useState('');
    const [newCompanyLogo, setNewCompanyLogo] = useState<File | null>(null);

    const [editCompanyName, setEditCompanyName] = useState('');

    const { data: companies, isLoading, isError } = useQuery<Company[]>({ queryKey: ['companies'], queryFn: getCompanies });

    const createCompanyMutation = useMutation({
        mutationFn: createCompany,
        onSuccess: (newCompany) => {
            queryClient.invalidateQueries({ queryKey: ['companies'] });
            setCreateFormVisible(false);
            setNewCompanyName('');
            setNewCompanyDomain('');
            setNewCompanyLogo(null);
            toast.success('Company created successfully! Now, create an admin.');
            navigate(APP_ROUTES.CREATE_COMPANY_ADMIN.replace(':id', newCompany.id));
        },
        onError: (error: Error) => { toast.error(`Failed to create company: ${error.message}`); }
    });

    const updateCompanyMutation = useMutation({ 
        mutationFn: updateCompany, 
        onSuccess: () => { 
            queryClient.invalidateQueries({ queryKey: ['companies'] }); 
            setEditModalOpen(false); 
            toast.success('Company updated successfully!');
        },
        onError: (error: Error) => {
            toast.error(`Failed to update company: ${error.message}`);
        }
    });

    const deactivateCompanyMutation = useMutation({ 
        mutationFn: deactivateCompany, 
        onSuccess: () => { 
            queryClient.invalidateQueries({ queryKey: ['companies'] }); 
            setDeactivateModalOpen(false); 
            toast.success('Company deactivated successfully!');
        },
        onError: (error: Error) => {
            toast.error(`Failed to deactivate company: ${error.message}`);
        }
    });

    const handleCreateCompany = (e: React.FormEvent) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('name', newCompanyName);
        if (newCompanyDomain) formData.append('root_domain', newCompanyDomain);
        if (newCompanyLogo) formData.append('logo', newCompanyLogo);
        createCompanyMutation.mutate(formData);
    };

    const handleUpdateCompany = (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedCompany) return;
        updateCompanyMutation.mutate({ id: selectedCompany.id, data: { name: editCompanyName } });
    };

    const handleDeactivateCompany = () => {
        if (!selectedCompany) return;
        deactivateCompanyMutation.mutate(selectedCompany.id);
    };

    const openEditModal = (e: React.MouseEvent, company: Company) => {
        e.stopPropagation();
        setSelectedCompany(company);
        setEditCompanyName(company.name);
        setEditModalOpen(true);
    };

    const openDeactivateModal = (e: React.MouseEvent, company: Company) => {
        e.stopPropagation();
        setSelectedCompany(company);
        setDeactivateModalOpen(true);
    };

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold">{t('company.title')}</h1>
                <button onClick={() => setCreateFormVisible(true)} className="px-4 py-2 bg-indigo-600 text-white font-semibold rounded-lg shadow-md hover:bg-indigo-700">{t('company.createButton')}</button>
            </div>

            {isCreateFormVisible && (
                <div className="mb-6 p-4 bg-white rounded-lg shadow-md">
                    <h2 className="text-xl font-bold mb-4">New Company</h2>
                    <form onSubmit={handleCreateCompany} className="space-y-4">
                        <div>
                            <label htmlFor="company-name" className="block text-sm font-medium text-gray-700">Name *</label>
                            <input type="text" id="company-name" value={newCompanyName} onChange={(e) => setNewCompanyName(e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" required />
                        </div>
                        <div>
                            <label htmlFor="company-domain" className="block text-sm font-medium text-gray-700">Root Domain</label>
                            <input type="text" id="company-domain" value={newCompanyDomain} onChange={(e) => setNewCompanyDomain(e.target.value)} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm" />
                        </div>
                        <div>
                            <label htmlFor="company-logo" className="block text-sm font-medium text-gray-700">Logo</label>
                            <input type="file" id="company-logo" accept="image/*" onChange={(e) => setNewCompanyLogo(e.target.files ? e.target.files[0] : null)} className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100" />
                        </div>
                        <div className="flex justify-end space-x-2">
                            <button type="button" onClick={() => setCreateFormVisible(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md">Cancel</button>
                            <button type="submit" className="px-4 py-2 bg-indigo-600 text-white rounded-md" disabled={createCompanyMutation.isPending}>{createCompanyMutation.isPending ? 'Creating...' : 'Create'}</button>
                        </div>
                    </form>
                </div>
            )}

            <div className="bg-white rounded-lg shadow-md">
                <h2 className="text-xl font-bold p-4 border-b">{t('company.listTitle')}</h2>
                {isLoading ? <div className="p-4"><Loader /></div> : isError ? <p className="p-4 text-red-500">{t('company.error')}</p> : companies && companies.length > 0 ? (
                    <ul className="divide-y divide-gray-200">
                        {companies.map(company => (
                            <li key={company.id} className="p-4 hover:bg-gray-50 cursor-pointer" onClick={() => navigate(APP_ROUTES.COMPANY_DETAIL.replace(':id', company.id))}>
                                <div className="flex justify-between items-center">
                                    <div className="flex items-center space-x-4">
                                        {company.logo && <img src={company.logo} alt={company.name} className="w-10 h-10 rounded-full object-cover" />}
                                        <span className="font-medium">{company.name}</span>
                                    </div>
                                    <div className="space-x-2">
                                        <button onClick={(e) => openEditModal(e, company)} className="px-3 py-1 text-sm text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200">Edit</button>
                                        <button onClick={(e) => openDeactivateModal(e, company)} className="px-3 py-1 text-sm text-red-600 bg-red-100 rounded-md hover:bg-red-200">Deactivate</button>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                ) : (
                    <EmptyState title={t('company.noCompaniesTitle')} message={t('company.noCompaniesMsg')} />
                )}
            </div>

            {isEditModalOpen && selectedCompany && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center">
                    <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
                        <h2 className="text-xl font-bold mb-4">Edit Company</h2>
                        <form onSubmit={handleUpdateCompany}>
                            <label htmlFor="edit-company-name" className="block text-sm font-medium text-gray-700">Company Name</label>
                            <input 
                                type="text"
                                id="edit-company-name"
                                value={editCompanyName}
                                onChange={(e) => setEditCompanyName(e.target.value)}
                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm"
                                required
                            />
                            <div className="flex justify-end space-x-2 mt-6">
                                <button type="button" onClick={() => setEditModalOpen(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md">Cancel</button>
                                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-md" disabled={updateCompanyMutation.isPending}>
                                    {updateCompanyMutation.isPending ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {isDeactivateModalOpen && selectedCompany && (
                 <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center">
                    <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-md">
                        <h2 className="text-xl font-bold mb-4">Deactivate Company</h2>
                        <p>Are you sure you want to deactivate the company "<span className="font-bold">{selectedCompany.name}</span>"? This action cannot be undone.</p>
                        <div className="flex justify-end space-x-2 mt-6">
                            <button type="button" onClick={() => setDeactivateModalOpen(false)} className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md">Cancel</button>
                            <button onClick={handleDeactivateCompany} className="px-4 py-2 bg-red-600 text-white rounded-md" disabled={deactivateCompanyMutation.isPending}>
                                {deactivateCompanyMutation.isPending ? 'Deactivating...' : 'Deactivate'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompanyManagement;
